import * as Sentry from "@sentry/browser";
import React from "react";
import { useSelector } from "react-redux";
import { useAccountId, useOrganizationId } from "redux/auth/selectors";
import { fetchAllCampaignsStatuses } from "redux/campaignStatus/campaignStatusActions";
import { getCampaignsStatuses } from "redux/campaignStatus/statusServerSelectors";
import { useAppDispatch } from "redux/store";

type Args = {
  action;
  actionArgs?: Array<any>;
  hasStoredData?: boolean;
  useStoredData?: boolean;
};

function useReduxDataLoader({
  action,
  actionArgs = [],
  hasStoredData = false,
  useStoredData = true,
}: Args) {
  const [error, setError] = React.useState("");
  const [isLoading, setLoading] = React.useState(false);

  const organizationId = useOrganizationId();
  const accountId = useAccountId();
  const dispatch = useAppDispatch();

  React.useEffect(() => {
    let isMounted = true;

    if (useStoredData && hasStoredData) {
      return;
    }

    setLoading(true);

    dispatch(
      action(
        ...actionArgs,
        () => {
          if (!isMounted) return null;
          setLoading(false);
        },
        (error: Error) => {
          Sentry.captureException(error);
          if (!isMounted) return null;
          setError(error?.message ?? "An error has occured");
          setLoading(false);
        }
      )
    );

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, accountId]);

  return { error, isLoading };
}

export const useCampaignsStatusDataLoader = () => {
  const storedData = useSelector(getCampaignsStatuses);

  useReduxDataLoader({
    action: fetchAllCampaignsStatuses,
    useStoredData: true,
    hasStoredData: !!Object.keys(storedData || {}).length,
  });
};
